import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Tooltip from './common/Tooltip';
import DonutChart from './DonutChart';
import { formatCurrency, formatPercentage, parseFloatFromString } from '../utils/formatters';
import { positionsListNavState } from '../utils/positionsListNavState';

const SEGMENT_ORDER = ['not_planned', 'planned', 'approaching', 'triggered'];
const SEGMENT_LABELS = {
  not_planned: 'Not planned',
  planned: 'Planned',
  approaching: 'Approaching',
  triggered: 'Triggered',
};
const SEGMENT_COLORS = {
  not_planned: '#9CA3AF',
  planned: '#3B82F6',
  approaching: '#F59E0B',
  triggered: '#EF4444',
};

export function classifyExit(position) {
  const sellAmt = parseFloatFromString(position?.exitStrategy?.sellAmount || '0');
  const levels = position?.exitStrategy?.levels || [];
  const hasExit = sellAmt > 0 && levels.length > 0;
  if (!hasExit) return 'not_planned';

  const exitStatus = (position?.strategyStatus?.exitStatus || '').toLowerCase();
  if (exitStatus === 'triggered') return 'triggered';
  if (exitStatus === 'approaching') return 'approaching';
  return 'planned';
}

function classifyInvalidation(position) {
  const inv = position?.invalidationLevel;
  if (!inv?.id) return 'not_planned';
  if (inv.isPriceTriggered || inv.isTimeTriggered) return 'triggered';
  const remaining = inv.priceDropPercentRemaining != null ? parseFloatFromString(String(inv.priceDropPercentRemaining)) : null;
  if (remaining !== null && remaining <= 10) return 'approaching';
  return 'planned';
}

const PlanningStatus = ({ positions = [] }) => {
  const navigate = useNavigate();
  const nonZeroPositions = useMemo(
    () => positions.filter((p) => parseFloatFromString(p?.holdingsAmount || '0') > 0),
    [positions]
  );
  const positionsCount = nonZeroPositions.length;

  const exitBreakdown = useMemo(() => {
    const counts = { not_planned: 0, planned: 0, approaching: 0, triggered: 0 };
    const values = { not_planned: 0, planned: 0, approaching: 0, triggered: 0 };
    let sumPlannedValue = 0;
    nonZeroPositions.forEach((p) => {
      const seg = classifyExit(p);
      counts[seg]++;
      const hv = parseFloatFromString(p?.holdingsValue || '0');
      values[seg] += hv;
      const amt = parseFloatFromString(p?.holdingsAmount || '0');
      const sellAmt = parseFloatFromString(p?.exitStrategy?.sellAmount || '0');
      if (amt > 0 && sellAmt > 0 && hv > 0) {
        const frac = Math.max(0, Math.min(1, sellAmt / amt));
        sumPlannedValue += hv * frac;
      }
    });
    const sumValueAll = Object.values(values).reduce((a, b) => a + b, 0);
    const valueCoveragePct = sumValueAll > 0 ? (sumPlannedValue / sumValueAll) * 100 : 0;
    return { counts, values, valueCoveragePct, sumValueAll };
  }, [nonZeroPositions]);

  const invalidationBreakdown = useMemo(() => {
    const counts = { not_planned: 0, planned: 0, approaching: 0, triggered: 0 };
    const values = { not_planned: 0, planned: 0, approaching: 0, triggered: 0 };
    nonZeroPositions.forEach((p) => {
      const seg = classifyInvalidation(p);
      counts[seg]++;
      const hv = parseFloatFromString(p?.holdingsValue || '0');
      values[seg] += hv;
    });
    const sumValueAll = Object.values(values).reduce((a, b) => a + b, 0);
    const sumWithInv = values.planned + values.approaching + values.triggered;
    const valueCoveragePct = sumValueAll > 0 ? (sumWithInv / sumValueAll) * 100 : 0;
    return { counts, values, valueCoveragePct, sumValueAll };
  }, [nonZeroPositions]);

  const exitCoveredCount = exitBreakdown.counts.planned + exitBreakdown.counts.approaching + exitBreakdown.counts.triggered;
  const invalidationCoveredCount =
    invalidationBreakdown.counts.planned + invalidationBreakdown.counts.approaching + invalidationBreakdown.counts.triggered;

  const exitDonutData = useMemo(() => {
    const total = exitBreakdown.sumValueAll || 1;
    return SEGMENT_ORDER.map((key) => {
      const count = exitBreakdown.counts[key] || 0;
      const value = exitBreakdown.values[key] || 0;
      const pct = total > 0 ? (value / total) * 100 : 0;
      const label = SEGMENT_LABELS[key].toLowerCase();
      const tooltip = `${count} position${count !== 1 ? 's' : ''} ${label} – ${formatCurrency(value)} (${formatPercentage(pct)})`;
      return {
        name: SEGMENT_LABELS[key],
        value,
        count,
        color: SEGMENT_COLORS[key],
        filterValue: key === 'not_planned' ? 'unset' : key === 'planned' ? 'set' : key,
        tooltip,
      };
    });
  }, [exitBreakdown]);

  const invalidationDonutData = useMemo(() => {
    const total = invalidationBreakdown.sumValueAll || 1;
    return SEGMENT_ORDER.map((key) => {
      const count = invalidationBreakdown.counts[key] || 0;
      const value = invalidationBreakdown.values[key] || 0;
      const pct = total > 0 ? (value / total) * 100 : 0;
      const label = SEGMENT_LABELS[key].toLowerCase();
      const tooltip = `${count} position${count !== 1 ? 's' : ''} ${label} – ${formatCurrency(value)} (${formatPercentage(pct)})`;
      return {
        name: SEGMENT_LABELS[key],
        value,
        count,
        color: SEGMENT_COLORS[key],
        filterValue: key === 'not_planned' ? 'unset' : key === 'planned' ? 'set' : key,
        tooltip,
      };
    });
  }, [invalidationBreakdown]);

  const LegendRow = ({ segment, filterKey }) => {
    const state =
      filterKey === 'exit'
        ? positionsListNavState({ exitStrategyFilter: segment.filterValue })
        : positionsListNavState({ invalidationFilter: segment.filterValue });
    return (
      <Tooltip title={segment.tooltip} position="top" arrow={true} theme="dark" size="small">
        <Link
          to="/positions"
          state={state}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
        >
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: segment.color }} />
          <span>{segment.name}</span>
          <span className="tabular-nums text-gray-500 dark:text-gray-400">({segment.count})</span>
        </Link>
      </Tooltip>
    );
  };

  const handleExitSegmentClick = (segment) => {
    navigate('/positions', { state: positionsListNavState({ exitStrategyFilter: segment.filterValue }) });
  };
  const handleInvalidationSegmentClick = (segment) => {
    navigate('/positions', { state: positionsListNavState({ invalidationFilter: segment.filterValue }) });
  };

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Exit Strategies</h4>
          <div className="flex items-start gap-4">
            <DonutChart
              data={exitDonutData}
              size={120}
              thickness={24}
              showLegend={false}
              showTotal={true}
              totalLabel="Positions"
              centerLabel="Positions"
              centerValue={`${exitCoveredCount}/${positionsCount}`}
              valueFormatter={formatCurrency}
              onSegmentClick={handleExitSegmentClick}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1.5 min-w-0">
              {exitDonutData.map((seg) => (
                <LegendRow key={seg.name} segment={seg} filterKey="exit" />
              ))}
            </div>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">
            Capital covered: {formatPercentage(exitBreakdown.valueCoveragePct)}
          </p>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Invalidation Strategies</h4>
          <div className="flex items-start gap-4">
            <DonutChart
              data={invalidationDonutData}
              size={120}
              thickness={24}
              showLegend={false}
              showTotal={true}
              totalLabel="Positions"
              centerLabel="Positions"
              centerValue={`${invalidationCoveredCount}/${positionsCount}`}
              valueFormatter={formatCurrency}
              onSegmentClick={handleInvalidationSegmentClick}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1.5 min-w-0">
              {invalidationDonutData.map((seg) => (
                <LegendRow key={seg.name} segment={seg} filterKey="invalidation" />
              ))}
            </div>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">
            Capital covered: {formatPercentage(invalidationBreakdown.valueCoveragePct)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlanningStatus;
